import { useState } from 'react';

import { useRecoilValue } from 'recoil';

import { cn } from 'lib/helpers/utility';
import useTimezone from 'lib/hooks/useTimezone';
import { formatDate } from 'lib/helpers/dateUtils';

import state from 'components/state';
import Avatar from 'components/shared/Avatar/Avatar';
import Button from 'components/shared/Button/Button';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';
import Typography from 'components/shared/Typography/Typography';

import { formatReplyTimestamp } from '../../../lib/helpers';
import { useActivityCreator } from '../lib/useActivityCreator';

import Replies from './Replies';

const Comment = ({
  comment = {},
  category,
  refId,
  onDelete,
  openCommentInputHandler,
  parentCommentId,
  isReply = false,
  parentCreatedAt
}) => {
  const timezone = useTimezone();
  const userState = useRecoilValue(state.userState);
  const permission = useRecoilValue(state.permissions);

  const currentUserId = permission?.admin_id || userState?.id;

  const [deleteWarning, setDeleteWarning] = useState(false);
  const [isRepliesExpanded, setIsRepliesExpanded] = useState(false);

  const { creator, created_at, updated_at, value } = comment;

  const { displayName, fullName, avatarProps } = useActivityCreator(comment);

  const commentDate = isReply
    ? formatReplyTimestamp(created_at, parentCreatedAt, timezone, formatDate)
    : formatDate(created_at, timezone, 'h:mma');

  const onToggleDeleteHandler = () => setDeleteWarning((prev) => !prev);

  const commentActions = [
    {
      icon: 'edit-2-mono',
      label: 'Edit',
      onClick: () => {
        openCommentInputHandler?.({
          commentId: comment.id,
          fullName,
          value,
          isReply,
          parentCommentId
        });
      }
    },
    { icon: 'trash-mono', label: 'Delete', onClick: onToggleDeleteHandler }
  ];

  const handleReplyClick = () => {
    openCommentInputHandler?.({ replying: true, commentId: comment.id, fullName });
  };

  const toggleReplies = () => {
    setIsRepliesExpanded((prev) => !prev);
  };

  return (
    <div className={cn('flex h-fit w-full flex-col items-start gap-2', isReply && 'gap-0.5 pr-2')}>
      <div className="flex w-full flex-row items-center gap-1">
        <Avatar
          size="small"
          isSquare={false}
          className="!h-[18px] !min-h-[18px] !w-[18px] !min-w-[18px] shrink-0"
          {...avatarProps}
        />

        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-1">
            <Typography variant="p" color="neutral-800" size="small">
              {displayName}
            </Typography>

            <Typography variant="p" color="neutral-600" size="small">
              {isReply ? 'replied:' : 'commented:'}
            </Typography>
          </div>

          <div className="flex items-center gap-2">
            {!isReply && (
              <Button
                size="xs"
                variant="link"
                text="Reply"
                onClick={handleReplyClick}
                className="!px-2"
                textClassName="text-xs font-500"
              />
            )}

            {currentUserId === creator?.id && (
              <Popover
                isFixed
                options={commentActions}
                icon="context-menu-vertical"
                buttonClassName="!h-6 !w-6 !min-w-6"
                position="left"
                isBorder={false}
                buttonTheme="transparent"
                activeClassNames={{ button: '!bg-primary-100' }}
              />
            )}

            <Typography color="neutral-500" size="small" className="shrink-0 whitespace-nowrap">
              {commentDate}
              {updated_at && created_at !== updated_at ? ' (edited)' : ''}
            </Typography>
          </div>
        </div>
      </div>

      <div
        className={
          isReply ? 'w-full' : 'w-[calc(100%+8px)] rounded-[4px] bg-neutral-75 px-2 pb-3 pt-1.5'
        }>
        <div className="flex flex-col gap-1.5">
          <Typography size="small" color="neutral-800" className="break-all">
            {value}
          </Typography>

          {!isReply && (
            <Replies
              commentId={comment.id}
              repliesCount={comment.replies_count}
              replyAuthors={comment.reply_authors}
              parentCreatedAt={created_at}
              category={category}
              refId={refId}
              isExpanded={isRepliesExpanded}
              onToggleExpanded={toggleReplies}
              openCommentInputHandler={openCommentInputHandler}
            />
          )}
        </div>
      </div>

      <Confirm
        isOpen={deleteWarning}
        handleClose={onToggleDeleteHandler}
        title="Delete this comment?"
        message="You cannot undo this action."
        iconColor="danger"
        variant="danger"
        icon="message-remove-mono"
        footerActions={{
          primary: {
            text: 'Delete',
            color: 'danger',
            onClick: () => {
              onDelete(comment.id);
              onToggleDeleteHandler();
            }
          }
        }}
      />
    </div>
  );
};

export default Comment;
